import React from 'react'
import { useParams } from 'react-router-dom'
import { COMPANY_ROUTES, ROUTES, PERSONAL_ROUTES } from 'client-portal/constants/routes'
import {
  useAuth,
  CompanyMemberRole,
  GroupMemberRole,
  useCompanyMember,
  CompanyIsAllowed,
  UserRole,
} from '@modmd/data'
import { PageLayout, PageNavLink } from './PageLayout'

interface Props {
  title?: string
  hasContentPadding?: boolean
}

interface Params {
  companyId: string
}

export const CompanyPageLayout: React.FC<Props> = ({ children, title, hasContentPadding }) => {
  const auth = useAuth()
  const { hasRoles } = useAuth()
  const params = useParams<Params>()
  const isSuperAdmin = hasRoles([UserRole.SUPER_ADMIN])
  const isCLS = hasRoles([UserRole.CLS])
  const { getUserCompaniesWithRole, getUserGroupsWithRole } = useAuth()
  const { companies, hasCompaniesWithRole } = getUserCompaniesWithRole({
    roles: [CompanyMemberRole.Owner, CompanyMemberRole.Manager],
  })
  const { groups } = getUserGroupsWithRole({
    roles: [GroupMemberRole.Manager, GroupMemberRole.Coordinator],
  })

  const companyId = companies[0]?.id ?? groups[0]?.companyId
  const { hasGroupsWithRole: isGroupManager } = getUserGroupsWithRole({
    companyIds: [companyId],
    roles: [GroupMemberRole.Manager],
  })
  const { data: isCompanyAllowed } = useCompanyMember({
    userId: auth.data.User.id,
  }) as CompanyIsAllowed

  return (
    <PageLayout
      navLinks={
        [
          isSuperAdmin || (isCompanyAllowed?.companyMember && companyId) || isCLS
            ? {
                to: `/${ROUTES.COMPANY}/${params.companyId}/${COMPANY_ROUTES.EVENT_CLEARANCE}`,
                label: 'FastPass',
              }
            : null,
          {
            to: `/${ROUTES.COMPANY}/${params.companyId}/${COMPANY_ROUTES.GROUPS}`,
            label: 'Groups',
          },
          {
            to: `/${ROUTES.COMPANY}/${params.companyId}/${COMPANY_ROUTES.TEST_SESSIONS}`,
            label: 'Test sessions',
          },
          {
            to: `/${ROUTES.COMPANY}/${params.companyId}/${COMPANY_ROUTES.EVENTS}`,
            label: 'Events',
          },
          hasCompaniesWithRole
            ? {
                to: `/${ROUTES.COMPANY}/${params.companyId}/${COMPANY_ROUTES.DETAILS}`,
                label: 'Settings',
              }
            : null,
          hasCompaniesWithRole || isGroupManager
            ? {
                to: `/${ROUTES.COMPANY}/${params.companyId}/${COMPANY_ROUTES.ANALYTICS}`,
                label: 'Analytics',
              }
            : null,
          { to: `/${ROUTES.PERSONAL}/${PERSONAL_ROUTES.SESSIONS}`, label: 'Personal' },
        ].filter(Boolean) as PageNavLink[]
      }
      title={title}
      hasContentPadding={hasContentPadding}
    >
      {children}
    </PageLayout>
  )
}
